/**
 * ProjectDateInputs - Core Component (Production-Ready)
 * Clean input component for date configuration.
 * Used in: Project workflow pages (ProjectSetupPage)
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DOMAIN KNOWLEDGE: Municipal Bond Date Structure (Michigan)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * This component configures the temporal foundation for municipal bond calculations.
 * These dates drive revenue capacity planning and backwards distribution.
 *
 * Key Concepts:
 *
 * 1. TAX COLLECTION SCHEDULE (Primary Driver)
 *    The tax collection schedule determines the ORDER and TIMING of all other dates.
 *    Michigan municipalities use either Summer or Winter tax collection.
 *
 *    This choice affects:
 *    - Which payment comes first (November or May)
 *    - Fiscal year end date
 *    - When revenue is assumed available for debt service
 *
 * 2. TAX YEAR (Property Assessment Year)
 *    - The year when property values are assessed for taxation
 *    - Example: Tax Year 2025 → property values assessed in 2025
 *    - This year drives all subsequent date calculations
 *    - Combined with tax collection schedule, determines all payment dates
 *
 * 3. PAYMENT DATES (SBQLP Mandated - Nov 1 / May 1)
 *    SBQLP (State Bond Qualification and Loan Program) requires Nov 1 and May 1 payments.
 *    The ORDER changes based on tax collection schedule:
 *
 *    SUMMER TAX COLLECTION (Tax Year 2025):
 *    - Bills sent: July 2025
 *    - First payment:  11/1/2025 (November of SAME year)
 *    - Second payment:  5/1/2026 (May of NEXT year)
 *    - Fiscal year end: 6/30/2026
 *
 *    WINTER TAX COLLECTION (Tax Year 2025):
 *    - Bills sent: December 2025
 *    - First payment:  5/1/2026 (May of NEXT year)
 *    - Second payment: 11/1/2026 (November of NEXT year)
 *    - Fiscal year end: 11/1/2026
 *
 *    Key insight: Two payments are always associated with a tax year.
 *    The dates themselves are for communication only - they don't affect calculations.
 *    What matters is the revenue available at each payment date.
 *
 *    Each payment date has:
 *    a) Date (YYYY-MM-DD)
 *    b) Revenue % (what portion of tax revenue arrives on this date)
 *    c) Payment Type (Principal + Interest OR Interest Only)
 *
 * 4. REVENUE PERCENTAGE DISTRIBUTION
 *    - Default: First payment 70%, Second payment 30% (user can modify)
 *    - Must sum to 100% for validation
 *    - Determines revenue capacity at each payment date
 *    - Used in backwards distribution algorithm
 *    - This is what actually drives the calculations (not the dates themselves)
 *    - Note: The first payment receives the larger percentage regardless of schedule
 *
 * 5. FISCAL YEAR END
 *    Calculated based on tax collection schedule:
 *    - Summer collection: 6/30 of year following tax year (e.g., 6/30/2026 for tax year 2025)
 *    - Winter collection: 11/1 of year following tax year (e.g., 11/1/2026 for tax year 2025)
 *    - Represents when all revenue from that tax year is assumed collected
 *
 * Why This Matters:
 * - Revenue capacity DRIVES bond sizing (not the other way around)
 * - Payment dates anchor the backwards distribution calculation
 * - Tax collection schedule determines payment order and fiscal year end
 * - Dates are for communication; revenue percentages drive calculations
 * - First period can be non-standard (30-360+ days) → requires normalization
 * - 30/360 day count convention used for interest calculations
 *
 * Processing Pipeline Impact:
 * - Tax year + collection schedule → generate payment dates and fiscal year end
 * - These dates generate DateRecords (via DateBuilder)
 * - DateRecords feed into Revenue Distribution (backwards pass)
 * - Distribution determines how much principal each payment can support
 * - Forward validation ensures DRF constraints are met
 *
 * Common Mistakes:
 * ❌ Don't assume fiscal year is always July 1 → June 30 (only true for summer)
 * ❌ Don't assume November is always the first payment (depends on schedule)
 * ❌ Don't mix calendar years with tax years
 * ❌ Don't assume dates affect calculations (revenue percentages do)
 * ✅ Use tax collection schedule to determine payment order
 * ✅ Payment dates are fixed by SBQLP: Nov 1 / May 1
 * ✅ Fiscal year end is calculated based on schedule
 *
 * For More Context:
 * @see docs/business-context/michigan-context.md - SBQLP requirements, tax collection rules
 * @see docs/business-context/process-overview.md - How dates feed into processing pipeline
 * @see docs/business-context/anti-patterns.md #9 - Tax year vs calendar year mistakes
 */

import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Heading,
  HStack,
  IconButton,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import type React from 'react';
import { useCallback } from 'react';

import { useProjectDateInputs } from '~/builders/project-dates/useProjectDateInputs';
import { DateInput, NumberInput, PercentInput } from '~/components/inputs';
import { useSidebar } from '~/components/layout/shared/SidebarContext';
import type { DateInputsForm, PaymentDateForm } from '~/types';

interface ProjectDateInputsProps {
  values: DateInputsForm;
  onUpdate: (values: DateInputsForm) => void;
  showValidation?: boolean;
  isDisabled?: boolean;
}

// Payment Date Row Component
interface PaymentDateRowProps {
  payment: PaymentDateForm;
  index: number;
  onUpdate: (field: keyof PaymentDateForm, value: string | boolean) => void;
  onMarkTouched: (field: string) => void;
  canRemove: boolean;
  onRemove: () => void;
}

const PaymentDateRow: React.FC<PaymentDateRowProps> = ({
  payment,
  index,
  onUpdate,
  onMarkTouched,
  canRemove,
  onRemove,
}) => {
  const handleDateChange = useCallback((value: string) => onUpdate('date', value), [onUpdate]);
  const handlePercentageChange = useCallback(
    (value: string) => onUpdate('revenuePercentage', value),
    [onUpdate]
  );
  const handleTypeChange = useCallback(
    (isInterestOnly: boolean) => onUpdate('isInterestOnly', isInterestOnly),
    [onUpdate]
  );

  return (
    <SimpleGrid columns={3} gap={3} alignItems="flex-end">
      <DateInput
        size="sm"
        value={payment.date}
        onChange={handleDateChange}
        onBlur={() => onMarkTouched(`payment-${index}-date`)}
        isRequired
      />
      <PercentInput
        size="sm"
        value={payment.revenuePercentage || ''}
        onChange={handlePercentageChange}
        onBlur={() => onMarkTouched(`payment-${index}-revenue`)}
        min={0}
        max={100}
        isRequired
      />
      <Box>
        <ButtonGroup attached size="sm" width="100%">
          <Button
            colorPalette={payment.isInterestOnly === false ? 'blue' : undefined}
            variant={payment.isInterestOnly === false ? 'solid' : 'outline'}
            onClick={() => handleTypeChange(false)}
            flex="1"
          >
            Principal+
          </Button>
          <Button
            colorPalette={payment.isInterestOnly === true ? 'blue' : undefined}
            variant={payment.isInterestOnly === true ? 'solid' : 'outline'}
            onClick={() => handleTypeChange(true)}
            flex="1"
          >
            Interest Only
          </Button>
        </ButtonGroup>
      </Box>
      <Box>
        {canRemove && (
          <IconButton
            aria-label="Remove payment date"
            size="sm"
            variant="ghost"
            colorScheme="red"
            onClick={onRemove}
          >
            ×
          </IconButton>
        )}
      </Box>
    </SimpleGrid>
  );
};

export const ProjectDateInputs: React.FC<ProjectDateInputsProps> = ({
  values,
  onUpdate,
  showValidation = false,
  isDisabled = false,
}) => {
  const { isExpanded } = useSidebar();

  // Extract all business logic to custom hook
  const { handlers, computed, refs, constants } = useProjectDateInputs({
    values,
    onUpdate,
    showValidation,
  });

  // Destructure for cleaner code
  const {
    handleInputChange,
    handlePaymentDateChange,
    handleRemovePaymentDate,
    handleStartingTaxYearBlur,
    markTouched,
  } = handlers;
  const {
    totalPercentage,
    displayPaymentDates,
    validationErrors,
    revenuePercentageStatus,
    revenuePercentageMessage,
    allFieldsTouched,
  } = computed;
  const { startingTaxYearInputRef } = refs;
  const { MIN_DISPLAY_YEAR, MAX_DISPLAY_YEAR, MIN_PAYMENT_DATES } = constants;

  return (
    <VStack
      align="stretch"
      boxShadow="md"
      gap={6}
      border="1px solid"
      borderColor="gray.200"
      p={4}
      borderRadius="md"
      bg={isDisabled ? 'gray.50' : undefined}
      pointerEvents={isDisabled ? 'none' : undefined}
      opacity={isDisabled ? 0.8 : 1}
    >
      <Heading size="md">Date Information</Heading>

      {/* Basic Date Information */}
      <VStack align="stretch" gap={3}>
        <SimpleGrid columns={3} gap={3} alignItems="flex-end">
          <NumberInput
            ref={startingTaxYearInputRef}
            size="sm"
            label={isExpanded ? 'Start Year' : 'Starting Tax Year'}
            value={values.startingTaxYear}
            onChange={(value) => handleInputChange('startingTaxYear', value)}
            onBlur={handleStartingTaxYearBlur}
            min={MIN_DISPLAY_YEAR}
            max={MAX_DISPLAY_YEAR}
            step={1}
            formatOptions={{
              useGrouping: false,
              maximumFractionDigits: 0,
            }}
          />

          <DateInput
            label={isExpanded ? 'FY End' : 'Fiscal Year End'}
            size="sm"
            value={values.fiscalYearEnd}
            onChange={(value) => handleInputChange('fiscalYearEnd', value)}
            onBlur={() => markTouched('fiscalYearEnd')}
          />

          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={1}>
              Tax Collection Schedule
            </Text>
            <ButtonGroup attached size="sm" width="100%">
              <Button
                colorPalette={values.taxCollectionSchedule === 'summer' ? 'blue' : undefined}
                variant={values.taxCollectionSchedule === 'summer' ? 'solid' : 'outline'}
                onClick={() => handleInputChange('taxCollectionSchedule', 'summer')}
                flex="1"
              >
                Summer
              </Button>
              <Button
                colorPalette={values.taxCollectionSchedule === 'winter' ? 'blue' : undefined}
                variant={values.taxCollectionSchedule === 'winter' ? 'solid' : 'outline'}
                onClick={() => handleInputChange('taxCollectionSchedule', 'winter')}
                flex="1"
              >
                Winter
              </Button>
            </ButtonGroup>
          </Box>
        </SimpleGrid>
      </VStack>

      {/* Payment Dates Section */}
      <Box>
        <SimpleGrid columns={3} gap={3} alignItems="flex-end" mb={-2}>
          <Text fontSize="sm" fontWeight="medium">
            Payment Dates
          </Text>
          <Box>
            <HStack gap={2} alignItems="center">
              <Badge
                colorScheme={
                  revenuePercentageStatus.status === 'complete'
                    ? 'green'
                    : revenuePercentageStatus.status === 'empty'
                      ? 'gray'
                      : 'red'
                }
              >
                {totalPercentage.toFixed(1)}%
              </Badge>
              {revenuePercentageStatus.status !== 'complete' && (
                <Text fontSize="xs" color={revenuePercentageStatus.color}>
                  {revenuePercentageMessage}
                </Text>
              )}
            </HStack>
          </Box>
          <Text fontSize="sm" fontWeight="medium">
            Payment Type
          </Text>
          <Box />
        </SimpleGrid>

        <VStack gap={2} align="stretch">
          {displayPaymentDates.map((payment, index) => (
            <PaymentDateRow
              key={`${payment.date}-${index}`}
              payment={payment}
              index={index}
              onUpdate={(field, value) => handlePaymentDateChange(index, field, value)}
              onMarkTouched={markTouched}
              canRemove={values.paymentDates.length > MIN_PAYMENT_DATES}
              onRemove={() => handleRemovePaymentDate(index)}
            />
          ))}
        </VStack>
      </Box>

      {/* Validation Messages */}
      {(showValidation || allFieldsTouched) && validationErrors.length > 0 && (
        <Box
          mt={4}
          p={3}
          bg="red.50"
          borderRadius="md"
          borderLeft="4px solid"
          borderColor="red.500"
        >
          <Text fontSize="xs" fontWeight="bold" color="red.600" mb={2}>
            Please fix the following issues:
          </Text>
          <VStack align="flex-start" gap={1}>
            {validationErrors.map((error) => (
              <Text key={error} fontSize="xs" color="red.600">
                • {error}
              </Text>
            ))}
          </VStack>
        </Box>
      )}
    </VStack>
  );
};

// Export types for use in other files
export type { DateInputsForm, PaymentDateForm };

// Backward compatibility export
export const DateInputs = ProjectDateInputs;
